import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';

// Default content fallbacks (subset of most critical items)
const DEFAULT_CONTENT = {
  // Landing
  'landing.hero.title': 'Find reliable trades fast',
  'landing.hero.subtitle': 'OnSiteHand helps employers find available tradespeople quickly and helps tradespeople get more work.',
  'landing.hero.cta_find_trades': 'Find Trades',
  'landing.hero.cta_get_work': 'Get Work',
  'landing.free_browsing.text': 'OnSiteHand is free to use for tradespeople and employers.',
  
  // Footer
  'footer.brand.tagline': 'Connecting employers with skilled tradespeople across the UK.',
  'footer.brand.email': 'support@onsitehand.co.uk',
  
  // Register
  'register.title': 'Create Account',
  'register.subtitle': 'Takes less than a minute',
  
  // Empty states
  'empty.no_messages': 'No messages yet',
  'empty.no_offers': 'No offers yet',
  
  // Credits
  'credits.balance_label': 'Credits',
  'credits.buy_more': 'Buy Credits',
};

const SiteContentContext = createContext({
  content: DEFAULT_CONTENT,
  loading: true,
  error: null,
  get: (key, defaultValue) => defaultValue || '',
  getPage: (page) => ({}),
  refresh: () => {},
});

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContent = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/site-content/public`);
      setContent(prevContent => ({
        ...DEFAULT_CONTENT,
        ...response.data
      }));
      setError(null);
    } catch (err) {
      console.warn('Failed to fetch site content, using defaults');
      setError(err);
      // Keep using defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Get a single content item with fallback
  const get = useCallback((key, defaultValue = '') => {
    const value = content[key];
    if (value === undefined || value === null) {
      return DEFAULT_CONTENT[key] || defaultValue;
    }
    return value;
  }, [content]);

  // Get all content for a page prefix
  const getPage = useCallback((page) => {
    const pageContent = {};
    Object.entries(content).forEach(([key, value]) => {
      if (key.startsWith(`${page}.`)) {
        // Extract just the part after the page prefix
        const shortKey = key.substring(page.length + 1);
        pageContent[shortKey] = value;
        pageContent[key] = value; // Also include full key
      }
    });
    return pageContent;
  }, [content]);

  const value = {
    content,
    loading,
    error,
    get,
    getPage,
    refresh: fetchContent,
  };

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
}

// Hook for components to access site content
export function useSiteContent() {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error('useSiteContent must be used within a SiteContentProvider');
  }
  return context;
}

// Shorthand hook to get a single content value
export function useContent(key, defaultValue = '') {
  const { get } = useSiteContent();
  return get(key, defaultValue);
}

// Hook to get all content for a page
export function usePageContent(page) {
  const { getPage } = useSiteContent();
  return getPage(page);
}

export default SiteContentContext;
